import type { CSSProperties, ReactNode } from 'react';
import { useSpotlight } from '../../hooks/useSpotlight';

/** Shared style for the gradient element every spotlit surface renders. */
export const spotlightGradientStyle = (visible: boolean): CSSProperties => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: 240,
  height: 240,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,107,0,0.11) 0%, transparent 70%)',
  pointerEvents: 'none',
  zIndex: 0,
  opacity: visible ? 1 : 0,
  transition: 'opacity 0.3s',
  willChange: 'transform',
});

interface SpotlightBoxProps {
  children: ReactNode;
  /** Applied to the inner content wrapper, not the positioning shell. */
  style?: CSSProperties;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * Wraps arbitrary content in a cursor-following radial highlight.
 *
 * The outer shell owns positioning; `style` lands on the inner wrapper so a
 * caller can set padding, background and borders without fighting the
 * `position: relative` the spotlight depends on. The caller's hover handlers
 * go on that same inner wrapper, so `event.currentTarget` is the styled
 * element they expect to mutate.
 */
export function SpotlightBox({ children, style = {}, onMouseEnter, onMouseLeave }: SpotlightBoxProps) {
  const { containerRef, spotRef, hovered, handlers } = useSpotlight<HTMLDivElement>();

  return (
    <div
      ref={containerRef}
      onMouseEnter={handlers.onMouseEnter}
      onMouseLeave={handlers.onMouseLeave}
      onMouseMove={handlers.onMouseMove}
      onTouchStart={handlers.onTouchStart}
      onTouchEnd={handlers.onTouchEnd}
      style={{ position: 'relative' }}
    >
      <div ref={spotRef} aria-hidden="true" style={spotlightGradientStyle(hovered)} />
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{ position: 'relative', zIndex: 1, ...style }}
      >
        {children}
      </div>
    </div>
  );
}
